// configurable-filtered-listing.js

/*
Controls display of list-items by matching facets and filters based on metadata-sets.

- Updates page URL so that filtered selection can be linked to directly.
- Adds pagination after selected amount: itemsPerPage.
- Requires own page setup. X-styling in x-feeds-lists.less.
- Test version at nyweb2. Used for AHO student projects.
*/

export default function configurableFilteredListing(localCallback) {
  const configurableFilteredListing = document.querySelector("#configurable-filtered-listing");
  if (!configurableFilteredListing) return;

  const lang = document.documentElement.lang;
  const dropdownsAll = Array.from(configurableFilteredListing.querySelectorAll("select"));
  const feedbackHeading = configurableFilteredListing.querySelector(".feedback h2");
  const listUl = configurableFilteredListing.querySelector(".configurable-list ul");
  const listItemsAll = Array.from(listUl.querySelectorAll("li"));

  // Pagination control
  let currentPage = 1;
  const itemsPerPage = 500;
  let filteredItems = listItemsAll; // Initialize with all items

  // Pagination: Create and include
  const pagination = document.createElement("nav");
  pagination.className = "pagination";
  pagination.ariaLabel = lang === "en" ? "Select page" : "Velg side";

  listUl.insertAdjacentElement("afterend", pagination);

  // If no list items, there is no point in continuing
  if (listItemsAll.length === 0) {
    console.debug("No items found.");
    return;
  }

  console.debug("Total items:", listItemsAll.length);

  // Function to filter items based on dropdown selections
  const filterItems = () => {
    filteredItems = listItemsAll.filter(li =>
      dropdownsAll.every(dropdown => {
        if (dropdown.selectedIndex === 0) return true;
        const selectedValue = dropdown.value;
        return Array.from(li.querySelectorAll(".facet")).some(
          facet => facet.textContent.trim() === selectedValue
        );
      })
    );

    console.debug("Active items:", filteredItems.length);

    // Add and remove "updated" class for transitions
    listItemsAll.forEach(li => {
      if (filteredItems.includes(li)) {
        li.classList.add("updated");
        setTimeout(() => {
          li.classList.remove("updated");
        }, 100);
      }
    });
  };

  // Function to display items based on pagination and filters
  const displayItems = () => {
    const filteredItemCount = filteredItems.length;
    const totalPages = Math.ceil(filteredItemCount / itemsPerPage);
    currentPage = Math.min(currentPage, totalPages);

    listItemsAll.forEach(li => li.classList.add("inactive"));

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = currentPage * itemsPerPage;
    const itemsToDisplay = filteredItems.slice(startIndex, endIndex);

    // Make all items on the current page visible
    itemsToDisplay.forEach(li => li.classList.remove('inactive'));

    renderPaginationButtons(totalPages);

    // Update feedback message with number of items found
    if (filteredItemCount === 0) {
      feedbackHeading.closest(".feedback").classList.add("no-hits");
      if (lang === "en") {
        feedbackHeading.textContent = "Sorry, nothing found";
      }
      else {
        feedbackHeading.textContent = "Beklager, fant ingenting";
      }
    }
    else {
      feedbackHeading.closest(".feedback").classList.remove("no-hits");
      if (lang === "en") {
        feedbackHeading.textContent = `Found ${filteredItemCount} items`;
      }
      else {
        feedbackHeading.textContent = `Fant ${filteredItemCount} elementer`;
      }
    }

    // Call local callback from host if provided
    if (typeof localCallback === "function") {
      localCallback();
    }
  };

  // Function to render pagination buttons
  const renderPaginationButtons = totalPages => {
    pagination.innerHTML = "";

    if (totalPages > 1) {
      for (let i = 1; i <= totalPages; i++) {
        const button = document.createElement("button");
        button.textContent = i;
        if (i === currentPage) {
          button.setAttribute("aria-current", "page");
        }
        button.addEventListener("click", () => {
          currentPage = i;
          displayItems(); // Ensure localCallback is called locally after this

          // Scroll to top when changing pages
          configurableFilteredListing.scrollIntoView();
        });
        pagination.appendChild(button);
      }
    }
  };

  // Function to update the URL with the current filter state
  const updateUrlWithFilters = () => {
    const params = new URLSearchParams();
    dropdownsAll.forEach(dropdown => {
      if (dropdown.selectedIndex !== 0) {  // Skip first option (all)
        params.append(dropdown.id, dropdown.value); // Use dropdown id
      }
    });
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState(null, "", newUrl);
  };

  // Initialize filters and pagination based on URL parameters
  const params = new URLSearchParams(window.location.search);
  dropdownsAll.forEach(dropdown => {
    const paramValue = params.get(dropdown.id);
    if (paramValue) {
      dropdown.value = paramValue;
    }
  });

  filterItems(); // Apply filters based on dropdown selection or URL
  displayItems(); // Display the initially filtered items

  // Add event listeners to dropdowns to apply filters on change
  dropdownsAll.forEach(dropdown => {
    dropdown.addEventListener("change", () => {
      currentPage = 1;
      filterItems();
      displayItems();
      updateUrlWithFilters(); // Update URL after applying new filters
    });
  });

  // Add class when JS is ready
  configurableFilteredListing.classList.add("js-ready");
}
